import { forwardRef, useMemo } from 'react';
import { useIntl } from 'react-intl';
import CounterBadge from '@alltrails/denali/components/CounterBadge';
import IconButton from '@alltrails/denali/components/IconButton';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Close from '@alltrails/denali/icons/Close';
import Layers from '@alltrails/denali/icons/Layers';
import { useAppSelector } from '../../redux';
import styles from './LayerButton.module.scss';

type Props = {
  isOpen: boolean;
  onClick: (isOpen: boolean) => void;
};

const LayerButton = forwardRef<HTMLButtonElement, Props>(({ isOpen, onClick }, ref) => {
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();
  const title = intl.formatMessage({ defaultMessage: 'Map options' });
  const { dynamicStyleIds, staticStyleIds } = useAppSelector(state => ({
    dynamicStyleIds: state.map.dynamicStyleIds,
    staticStyleIds: state.map.staticStyleIds
  }));
  const count = useMemo(() => {
    let totalCount = dynamicStyleIds.length + staticStyleIds.length;
    if (dynamicStyleIds.includes('groundConditions') && dynamicStyleIds.includes('surfaceType')) {
      // Applying ground conditions automatically applies surface type and doesn't let a user remove it, so only count it as one
      totalCount -= 1;
    }
    return totalCount;
  }, [dynamicStyleIds, staticStyleIds.length]);

  return (
    <div className={styles.container}>
      {isOpen && !isMobile ? (
        <IconButton
          ref={ref}
          icon={{ Component: Close }}
          onClick={() => onClick(false)}
          title={intl.formatMessage({ defaultMessage: 'Close map options' })}
          variant="elevated"
        />
      ) : (
        <>
          <IconButton ref={ref} icon={{ Component: Layers }} onClick={() => onClick(true)} testId="map-options" title={title} variant="elevated" />
          {!!count && <CounterBadge className={styles.counterBadge} count={count} testId="map-options-count" color="accent" round />}
        </>
      )}
    </div>
  );
});

export default LayerButton;
